import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../api'; // Assuming 'api' is your axios instance

// NOTE: If getCaptcha is not exported from api.js, define it here or ensure it's imported correctly
// import { getCaptcha } from '../api'; 

const ResetPasswordPage = () => {
    const location = useLocation();
    const email = new URLSearchParams(location.search).get('email');
    
    const [otp, setOtp] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const navigate = useNavigate();

    // --- CAPTCHA STATE ---
    const [showCaptcha, setShowCaptcha] = useState(false);
    const [captchaImage, setCaptchaImage] = useState(null);
    const [captchaId, setCaptchaId] = useState(null);
    const [captchaValue, setCaptchaValue] = useState("");

    // --- CAPTCHA FUNCTION ---
    const fetchCaptcha = async () => {
        try {
            // Call your backend endpoint: /vres/auth/captcha
// inside fetchCaptcha function
            const response = await api.get('/vres/auth/captcha');
            setCaptchaId(response.data.id);
            setCaptchaImage(`data:image/png;base64,${response.data.image}`);
            setCaptchaValue('');
        } catch (error) {
            console.error("Failed to load captcha", error);
            toast.error("Could not load security check.");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            toast.error('Passwords do not match.');
            return;
        }

        // 1. Validation: If Captcha is visible, it must be filled
        if (showCaptcha && !captchaValue) {
            toast.warning("Please enter the security code.");
            return;
        }

        const toastId = toast.loading("Resetting your password...");
        
        // 2. Prepare Payload
        const payload = { 
            email, 
            otp, 
            newPassword: password,
            // Include captcha fields (Backend DTO expects these names)
            captchaId: showCaptcha ? captchaId : null,
            captchaValue: showCaptcha ? captchaValue : null
        };

        try {
            const response = await api.post('/vres/auth/reset-password', payload);

            if (response.status === 200) {
                toast.update(toastId, { render: "Password has been reset successfully!", type: "success", isLoading: false, autoClose: 3000 });
                navigate('/login'); 
            }
        } catch (error) {
            console.error("Reset password API error:", error);
            
            // --- UPDATED ERROR HANDLING ---
            // The backend returns a simple string (e.g., "Invalid or expired OTP. Captcha required")
            // We must check this string to decide if we show the Captcha.
            
            let errorMessage = "An error occurred. Please try again.";
            
            if (error.response && error.response.data) {
                // If backend returns a JSON object with a message field
                if (typeof error.response.data === 'object' && error.response.data.message) {
                    errorMessage = error.response.data.message;
                } 
                // If backend returns a raw string (which your Controller does)
                else if (typeof error.response.data === 'string') {
                    errorMessage = error.response.data;
                }
            }

            // Check if the error message contains the trigger phrase
            if (errorMessage.includes("Captcha required") || errorMessage.includes("Invalid Captcha")) {
                
                toast.update(toastId, { 
                    render: errorMessage, // "Invalid OTP. Captcha required"
                    type: "warning", 
                    isLoading: false, 
                    autoClose: 4000 
                });
                
                // Enable Captcha
                if (!showCaptcha) {
                    setShowCaptcha(true);
                    fetchCaptcha(); // Load the first image
                } else {
                    fetchCaptcha(); // Refresh image if they failed the captcha check
                    setCaptchaValue('');
                }
            } else {
                // Standard Error (e.g., "Invalid OTP" 1st attempt)
                toast.update(toastId, { render: errorMessage, type: "error", isLoading: false, autoClose: 3000 });
            }
        }
    };

    // --- STYLES INJECTION ---
    const PageStyleBlock = () => (
        <style>{`
            .rp-input:focus {
                border-color: #20c997 !important;
                box-shadow: 0 0 0 3px rgba(32, 201, 151, 0.1) !important;
                outline: none;
            }
            .rp-btn:hover:not(:disabled) { opacity: 0.9; }
            .rp-btn:disabled { opacity: 0.6; cursor: not-allowed; }
            .rp-link:hover { text-decoration: underline; color: #0056b3; }
        `}</style>
    );

    return (
        <div style={styles.formWrapper}>
            <PageStyleBlock />
            
            <div style={styles.formCard}>
                <h2 style={styles.formTitle}>Create New Password</h2>
                <p style={{ textAlign: 'center', color: '#636e72', marginBottom: '25px', fontSize: '0.9rem' }}>
                    An OTP has been sent to <b>{email}</b>.
                </p>

                <form onSubmit={handleSubmit}>
                    
                    <div style={styles.formGroup}>
                        <label htmlFor="otp" style={styles.formLabel}>OTP</label>
                        <input
                            type="text"
                            id="otp"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            placeholder="Enter the 6-digit code"
                            className="rp-input"
                            style={styles.formControl}
                            required 
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label htmlFor="password" style={styles.formLabel}>New Password</label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter your new password"
                            className="rp-input"
                            style={styles.formControl}
                            required 
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label htmlFor="confirmPassword" style={styles.formLabel}>Confirm New Password</label>
                        <input
                            type="password"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm your new password"
                            className="rp-input"
                            style={styles.formControl}
                            required 
                        />
                    </div>

                    {/* --- CAPTCHA UI BLOCK --- */}
                    {showCaptcha && (
                        <div style={{background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e0e0e0'}}>
                            <p style={{margin: '0 0 10px 0', fontSize: '0.85rem', color: '#555', fontWeight: '600'}}>Security Check Required</p>
                            <div style={{display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px'}}>
                                {captchaImage ? 
                                    <img src={captchaImage} alt="Captcha" style={{border: '1px solid #ccc', borderRadius: '5px', height: '45px'}} /> 
                                    : <span style={{fontSize: '0.8rem'}}>Loading...</span>
                                }
                                <button type="button" onClick={fetchCaptcha} style={{background: 'none', border: 'none', color: '#20c997', fontWeight: 'bold', cursor: 'pointer', fontSize: '1.2rem'}} title="Refresh Image">↻</button>
                            </div>
                            <input 
                                type="text" 
                                placeholder="Enter characters from image" 
                                value={captchaValue} 
                                onChange={(e) => setCaptchaValue(e.target.value)} 
                                className="rp-input"
                                style={{...styles.formControl, padding: '10px'}} 
                            />
                        </div>
                    )}

                    <div style={styles.btnGroup}>
                        <button 
                            type="submit" 
                            className="rp-btn"
                            style={{ ...styles.btn, ...styles.btnPrimary }}
                        >
                            Reset Password
                        </button>
                    </div>

                    <div style={{ marginTop: "20px", textAlign: "center", fontSize: "0.9rem" }}>
                        <Link to="/login" className="rp-link" style={{ color: "#007bff", textDecoration: "none", fontWeight: "600" }}>
                            ← Back to Login
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- STYLES (Consistent with Teal Theme) ---
const styles = {
    formWrapper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center', 
        width: '100%',
        minHeight: '100vh', 
        padding: '20px',
        boxSizing: 'border-box',
        fontFamily: "'Segoe UI', Roboto, sans-serif",
        backgroundColor: '#f0fdf9', 
    },
    formCard: {
        background: 'white',
        width: '100%',
        maxWidth: '450px', 
        padding: '40px',
        borderRadius: '12px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
        borderTop: '5px solid #20c997', 
        margin: '0 auto',
    },
    formTitle: {
        textAlign: 'center',
        marginBottom: '10px',
        fontSize: '1.5rem',
        color: '#007bff', 
        fontWeight: '700',
        marginTop: 0,
    },
    formGroup: {
        marginBottom: '20px',
    },
    formLabel: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#2d3436', 
        fontSize: '0.9rem',
    },
    formControl: {
        width: '100%',
        padding: '12px 15px',
        border: '1px solid #dcdcdc',
        borderRadius: '6px',
        fontSize: '0.95rem',
        transition: 'all 0.3s',
        outline: 'none',
        boxSizing: 'border-box',
        color: '#2d3436',
        backgroundColor: '#fff',
    },
    btnGroup: {
        marginTop: '25px',
    },
    btn: {
        width: '100%',
        padding: '12px',
        borderRadius: '6px',
        fontSize: '1rem',
        fontWeight: '600',
        cursor: 'pointer',
        border: 'none',
        transition: 'opacity 0.2s',
    },
    btnPrimary: {
        backgroundColor: '#007bff', 
        color: 'white',
    },
};

export default ResetPasswordPage;